import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import * as mapbox from 'mapbox-gl';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { formatDateStandard } from 'src/app/shared/standard-date.pipe';
import { InternalMappingData, ShiftOrderDetails, ShiftService, Waypoint } from '../shift.service';

const STREETS = 'mapbox://styles/mapbox/streets-v11';
const SATELLITE = 'mapbox://styles/mapbox/satellite-v9';

@Component({
  selector: 'lohi-shift-map-internal',
  templateUrl: './shift-map.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftMapComponent implements OnInit, OnDestroy, AfterViewInit {
  private internalHighlightedOrderId: number;
  private map: mapbox.Map;
  private mapInfo: InternalMappingData;
  private destroy$$ = new Subject();
  private waypointMarker: mapboxgl.Marker;
  private streets = true;
  @Input() public set highlightedOrderId(orderId: number) {
    this.internalHighlightedOrderId = orderId;
    if (this.map) {
      this.updateMapWaypointSource();
    }
  }
  public get highlightedOrderId(): number {
    return this.internalHighlightedOrderId;
  }
  @Input() public set activeWaypoint(waypoint: Waypoint) {
    if (!this.map) {
      return;
    }
    if (waypoint) {
      if (this.waypointMarker) {
        this.waypointMarker.setLngLat({ lng: waypoint.longitude, lat: waypoint.latitude });
      } else {
        this.waypointMarker = new mapbox.Marker()
          .setLngLat({ lng: waypoint.longitude, lat: waypoint.latitude })
          .addTo(this.map);
      }
    } else {
      if (this.waypointMarker) {
        this.waypointMarker.remove();
        this.waypointMarker = null;
      }
    }
  }
  @ViewChildren('map') private mapElements: QueryList<ElementRef<HTMLDivElement>>;

  constructor(private shiftService: ShiftService) {
    (mapbox as any).accessToken =
      'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.initMap();
  }

  private async initMap() {
    // Make sure we have a map element
    if (this.mapElements.length === 0) {
      await this.mapElements.changes
        .pipe(
          filter((changes: QueryList<any>) => {
            return changes.length > 0;
          }),
          take(1),
        )
        .toPromise();
    }

    const options: mapbox.MapboxOptions = {
      container: this.mapElements.first.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
    };

    this.map = new mapbox.Map(options);
    this.map.setZoom(3);
    this.map.setCenter({ lat: 39.8283, lng: -98.5795 });
    this.map.addControl(new mapbox.NavigationControl());
    this.map.loadImage('/assets/images/hazard.png', (error, image) => {
      this.map.addImage('hazard', image);
    });
    this.map.loadImage('/assets/images/hazard_liked.jpg', (error, image) => {
      this.map.addImage('hazardLiked', image);
    });
    this.listenToMapInfo();

    const popUp = new mapbox.Popup({
      closeButton: false,
      closeOnClick: false,
    });
    this.map.on('mouseenter', 'hazardsReported', (e) => {
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const description = e.features[0].properties.description;

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      popUp.setLngLat(coordinates).setHTML(description).addTo(this.map);
    });

    this.map.on('mouseleave', 'hazardsReported', () => {
      popUp.remove();
    });

    this.map.on('mouseenter', 'hazardsLiked', (e) => {
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const description = e.features[0].properties.description;

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      popUp.setLngLat(coordinates).setHTML(description).addTo(this.map);
    });

    this.map.on('mouseleave', 'hazardsLiked', () => {
      popUp.remove();
    });
  }

  public toggleStyle() {
    if (this.streets) {
      this.map.setStyle(SATELLITE);
      this.streets = false;
    } else {
      this.map.setStyle(STREETS);
      this.streets = true;
    }
  }

  private listenToMapInfo() {
    this.shiftService.mapInfo$.pipe(takeUntil(this.destroy$$)).subscribe((mapInfo) => {
      this.mapInfo = mapInfo;
      this.calculateMap();
    });
  }

  private calculateMap() {
    if (!this.mapInfo) {
      return;
    }
    this.updateBounds();
    this.updateMapShiftSource();
    this.updateMapMineSource();
    this.updateMapFracSource();
    this.updateMapWaypointSource();
    this.updateHazardSource();
    this.updateHazardLikedSource();
  }

  private updateBounds() {
    const bounds = new mapbox.LngLatBounds();
    (this.mapInfo.waypoints || []).forEach((waypoint) => {
      bounds.extend([waypoint.longitude, waypoint.latitude]);
    });
    this.map.setMaxZoom(9);
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 100 });
      this.map.setMaxZoom(22);
    }
  }

  private updateMapWaypointSource() {
    if (this.map.getLayer('beforeLayer')) {
      this.map.removeLayer('beforeLayer').removeSource('beforeSource');
    }
    if (this.map.getLayer('afterLayer')) {
      this.map.removeLayer('afterLayer').removeSource('afterSource');
    }
    if (this.map.getLayer('activeLayer')) {
      this.map.removeLayer('activeLayer').removeSource('activeSource');
    }
    const sources = this.getWaypointSources();
    this.map.addSource('beforeSource', sources.before);
    this.map.addSource('activeSource', sources.active);
    this.map.addSource('afterSource', sources.after);
    this.addWaypointLayer('before');
    this.addWaypointLayer('after');
    this.addWaypointLayer('active'); // active goes on top of everything
  }

  private updateHazardSource() {
    if (this.map.getLayer('hazardsReported')) {
      this.map.removeLayer('hazardsReported').removeSource('hazardsReported');
    }
    const hazardSource = this.getHazardsSource();
    this.map.addSource('hazardsReported', hazardSource);
    this.map.addLayer({
      id: 'hazardsReported',
      type: 'symbol',
      source: 'hazardsReported',
      layout: {
        'icon-image': ['get', 'imageName'],
        'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', 50, 1]],
      },
    });
  }

  private updateHazardLikedSource() {
    if (this.map.getLayer('hazardsLiked')) {
      this.map.removeLayer('hazardsLiked').removeSource('hazardsLiked');
    }
    const hazardSource = this.getHazardsLikedSource();
    this.map.addSource('hazardsLiked', hazardSource);
    this.map.addLayer({
      id: 'hazardsLiked',
      type: 'symbol',
      source: 'hazardsLiked',
      layout: {
        'icon-image': 'hazardLiked',
        'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', 100, 1]],
      },
    });
  }

  private addWaypointLayer(layerType: 'before' | 'active' | 'after') {
    const heatMapColors = [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0,
      'rgba(0, 100, 0, 0)',
      0.5,
      'rgba(0, 100, 0, 0.5)',
      1,
      'rgba(0, 100, 0, 0.8)',
    ];
    if (layerType === 'active') {
      heatMapColors[4] = 'rgba(0, 255, 0, 0)';
      heatMapColors[6] = 'rgba(0, 255, 0, 0.5)';
      heatMapColors[8] = 'rgba(0, 255, 0, 0.8)';
    }
    this.map.addLayer({
      id: `${layerType}Layer`,
      type: 'heatmap',
      source: `${layerType}Source`,
      paint: {
        'heatmap-color': heatMapColors as any,
        'heatmap-radius': 3,
      },
    });
  }

  private updateMapFracSource() {
    if (this.map.getLayer('fracs')) {
      this.map.removeLayer('fracs').removeLayer('frac-labels').removeSource('fracs');
    }
    const fracSource = this.getFracSource();
    this.map.addSource('fracs', fracSource);
    this.map.addLayer({
      id: `fracs`,
      type: 'circle',
      source: `fracs`,
      paint: {
        'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', ['get', 'radius'], 1]],
        'circle-color': 'rgba(240, 79, 15, .5)',
      },
    });
    this.map.addLayer({
      id: 'frac-labels',
      type: 'symbol',
      source: 'fracs',
      layout: {
        'text-field': ['get', 'id'],
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 0.5,
        'text-justify': 'center',
      },
    });
  }

  private updateMapMineSource() {
    if (this.map.getLayer('mines')) {
      this.map.removeLayer('mines').removeLayer('mine-labels').removeSource('mines');
    }
    const mineSource = this.getMineSource();
    this.map.addSource('mines', mineSource);
    this.map.addLayer({
      id: `mines`,
      type: 'circle',
      source: `mines`,
      paint: {
        'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', ['get', 'radius'], 1]],
        'circle-color': 'rgba(8, 169, 233, 0.5)',
      },
    });
    this.map.addLayer({
      id: 'mine-labels',
      type: 'symbol',
      source: 'mines',
      layout: {
        'text-field': ['get', 'id'],
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 0.5,
        'text-justify': 'center',
      },
    });
  }

  private updateMapShiftSource() {
    if (this.map.getLayer('shiftInfo')) {
      this.map.removeLayer('shiftInfo').removeSource('shiftInfo');
    }
    const shiftSource = this.getShiftStartSource();
    this.map.addSource('shiftInfo', shiftSource);
    this.map.addLayer({
      id: `shiftInfo`,
      type: 'circle',
      source: `shiftInfo`,
      paint: {
        'circle-radius': ['interpolate', ['exponential', 2], ['zoom'], 0, 0, 20, ['*', ['get', 'radius'], 1]],
        'circle-color': 'rgba(83, 104, 122, 0.2)',
      },
    });
  }

  private getWaypointSources(): {
    before: mapboxgl.GeoJSONSourceRaw;
    active: mapboxgl.GeoJSONSourceRaw;
    after: mapboxgl.GeoJSONSourceRaw;
  } {
    let activeLowerBound = this.mapInfo.shiftInfo.startedAt;
    let activeUpperBound = this.mapInfo.shiftInfo.endedAt;
    if (this.highlightedOrderId) {
      const order = this.mapInfo.orders.find((o) => o.id === this.highlightedOrderId);
      if (order) {
        activeLowerBound = order.acceptedAt;
        activeUpperBound = order.endedAt;
      }
    }
    const beforeWaypoints = [];
    const activeWaypoints = [];
    const afterWaypoints = [];
    for (const waypoint of this.mapInfo.waypoints || []) {
      if (!waypoint) {
        continue;
      }
      if (waypoint.timestamp < activeLowerBound) {
        beforeWaypoints.push(waypoint);
      } else if (waypoint.timestamp >= activeLowerBound && waypoint.timestamp < activeUpperBound) {
        // non-inclusive
        activeWaypoints.push(waypoint);
      } else {
        afterWaypoints.push(waypoint);
      }
    }

    const beforeData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: beforeWaypoints.map(waypointToFeature),
    };
    const beforeSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: beforeData,
    };

    const activeData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: activeWaypoints.map(waypointToFeature),
    };

    const activeSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: activeData,
    };

    const afterData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: afterWaypoints.map(waypointToFeature),
    };

    const afterSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: afterData,
    };

    return {
      before: beforeSource,
      active: activeSource,
      after: afterSource,
    };
  }

  private getFracSource(): mapboxgl.GeoJSONSourceRaw {
    // Unique frac names
    const orders = (this.mapInfo.orders || []).reduce((acc, order) => {
      acc[order.fracName] = order;
      return acc;
    }, {} as Record<string, ShiftOrderDetails>);
    const fracData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: Object.values(orders).map((order) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [order.fracLng, order.fracLat],
        },
        properties: {
          id: order.fracName,
          radius: metersToPixelsAtMaxZoom(order.fracRadius, order.fracLat),
        },
      })),
    };
    const fracSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: fracData,
    };

    return fracSource;
  }

  private getMineSource(): mapboxgl.GeoJSONSourceRaw {
    // Unique mine names
    const orders = (this.mapInfo.orders || []).reduce((acc, order) => {
      acc[order.mineName] = order;
      return acc;
    }, {} as Record<string, ShiftOrderDetails>);
    const mineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: Object.values(orders).map((order) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [order.mineLng, order.mineLat],
        },
        properties: {
          id: order.mineName,
          radius: metersToPixelsAtMaxZoom(order.mineRadius, order.mineLat),
        },
      })),
    };
    const mineSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: mineData,
    };

    return mineSource;
  }

  private getShiftStartSource(): mapboxgl.GeoJSONSourceRaw {
    const mineData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [this.mapInfo.shiftInfo.startLng, this.mapInfo.shiftInfo.startLat],
          },
          properties: {
            id: 'Shift Start',
            radius: metersToPixelsAtMaxZoom(this.mapInfo.shiftInfo.checkedInRadius, this.mapInfo.shiftInfo.startLat),
          },
        },
      ],
    };
    return {
      type: 'geojson',
      data: mineData,
    };
  }

  private getHazardsSource(): mapboxgl.GeoJSONSourceRaw {
    const hazardData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: (this.mapInfo.hazardsReported || []).map((hazard) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [hazard.location.longitude, hazard.location.latitude],
        },
        properties: {
          description: `${formatDateStandard(
            hazard.reportedAt,
          )} - Reported a ${hazard.type.toUpperCase()} Hazard which received ${hazard.likeCount} ${
            +hazard.likeCount === 1 ? 'like' : 'likes'
          }`,
          imageName: 'hazard',
        },
      })),
    };
    const hazardSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: hazardData,
    };

    return hazardSource;
  }

  private getHazardsLikedSource(): mapboxgl.GeoJSONSourceRaw {
    const hazardData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: 'FeatureCollection',
      features: (this.mapInfo.hazardsLiked || []).map((hazard) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [hazard.location.longitude, hazard.location.latitude],
        },
        properties: {
          description: `${formatDateStandard(hazard.likedAt)} - Liked a ${hazard.type.toUpperCase()} Hazard`,
        },
      })),
    };
    const hazardSource: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: hazardData,
    };

    return hazardSource;
  }
}

function waypointToFeature(waypoint: Waypoint): GeoJSON.Feature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [waypoint.longitude, waypoint.latitude],
    },
    properties: {
      id: `${waypoint.id}`,
      time: new Date(waypoint.timestamp).getTime(),
    },
  };
}

// From https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js
function metersToPixelsAtMaxZoom(meters: number, latitude: number) {
  return meters / (78271.484 / 2 ** 20) / Math.cos((latitude * Math.PI) / 180);
}
